import type { AnalysisResult, C1ImportDraft } from "@estada/plan-analyzer";
import type { C1_PointData } from "../types/C1_ModelTypes";

/**
 * Import accepted PDF walls that were not used for an imported room contour.
 * Use collection A, just like the analyser's room draft, to exclude discarded
 * candidates. Without collections, both ordinary and independently detected
 * walls are active. Coordinates use the same origin and scale as the rooms.
 */
export function importedFreeWalls(analysis: AnalysisResult | undefined, draft: C1ImportDraft): { start: C1_PointData; end: C1_PointData }[] {
  if (!analysis) return [];
  const walls = analysis.wallCollections?.A ?? [...(analysis.walls ?? []), ...(analysis.freeWalls ?? [])];
  const used = new Set(draft.rooms.flatMap(room => room.walls.flatMap(wall => wall.sourceWallIds)));
  const unit = analysis.source.metersPerPoint;
  if (!unit || !Number.isFinite(unit) || unit <= 0) return [];
  const point = (value: C1_PointData) => ({ x: (value.x - draft.originPdf.x) * unit, y: (value.y - draft.originPdf.y) * unit });
  const seen = new Set<string>();
  return walls.flatMap(wall => {
    if (used.has(wall.id)) return [];
    const start = point(wall.a), end = point(wall.b);
    if (![start.x, start.y, end.x, end.y].every(Number.isFinite) || Math.hypot(end.x - start.x, end.y - start.y) < 0.01) return [];
    const key = [start, end].map(value => `${Math.round(value.x * 1000)},${Math.round(value.y * 1000)}`).sort().join("/");
    if (seen.has(key)) return [];
    seen.add(key);
    return [{ start, end }];
  });
}
